import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import logger from '../../lib/logger';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ClipboardCheck, ChevronRight, ChevronLeft, Check, Loader2, X, Building2,
  BarChart3, Target, Settings2, LineChart, Users
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const PILLAR_ICONS = {
  pillar_1: BarChart3,
  pillar_2: Target,
  pillar_3: Settings2,
  pillar_4: LineChart,
  pillar_5: Users,
};

function getBand(score) {
  if (score >= 75) return 'Optimized';
  if (score >= 50) return 'Managed';
  if (score >= 25) return 'Developing';
  return 'Emerging';
}

function getBandColor(band) {
  if (band === 'Optimized') return 'text-cyan-600';
  if (band === 'Managed') return 'text-teal-600';
  if (band === 'Developing') return 'text-amber-600';
  return 'text-slate-600';
}

function getBarColor(score) {
  if (score >= 75) return '#06b6d4';
  if (score >= 50) return '#14b8a6';
  if (score >= 25) return '#f59e0b';
  return '#94a3b8';
}

export function MaturityAssessmentOverlay({ onClose, onComplete, preselectedOrgId }) {
  const { isSuperAdmin } = useAuth();
  const [pillars, setPillars] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(preselectedOrgId || '');
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [currentPillar, setCurrentPillar] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(preselectedOrgId ? 'questions' : 'org-select');

  useEffect(() => {
    const fetches = [api.get('/maturity-assessment/pillars')];
    if (isSuperAdmin) {
      fetches.push(api.get('/rbac/organizations'));
    }
    Promise.all(fetches).then(([pillarsRes, orgsRes]) => {
      setPillars(pillarsRes.data.pillars);
      if (orgsRes) setOrganizations(orgsRes.data.organizations || []);
      if (!isSuperAdmin || preselectedOrgId) setStep('questions');
    }).catch(err => logger.error('Error loading assessment:', err))
      .finally(() => setLoading(false));
  }, [isSuperAdmin, preselectedOrgId]);

  const totalDimensions = pillars.reduce((acc, p) => acc + p.dimensions.length, 0);
  const answeredCount = Object.keys(answers).length;
  const progress = totalDimensions > 0 ? (answeredCount / totalDimensions) * 100 : 0;
  const allAnswered = answeredCount === totalDimensions;
  const pillar = pillars[currentPillar];
  const pillarAnswered = pillar ? pillar.dimensions.filter(d => answers[d.id] !== undefined).length : 0;

  const handleSelect = (dimId, level) => {
    setAnswers(prev => ({ ...prev, [dimId]: level }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const payload = { answers };
      if (selectedOrg) payload.organization_id = selectedOrg;
      const res = await api.post('/maturity-assessment/submit', payload);
      setResult(res.data);
      toast.success(`Assessment complete: ${Math.round(res.data.overall_score)} (${res.data.maturity_band})`);
    } catch (err) {
      logger.error('Error submitting assessment:', err);
      toast.error('Failed to submit assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDone = () => {
    if (onComplete && result) onComplete(result);
    onClose();
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-40 bg-white flex items-center justify-center" style={{ top: '64px', left: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-teal-500" />
      </div>
    );
  }

  const selectedOrgName = organizations.find(o => o.id === selectedOrg)?.name || '';

  return (
    <div className="fixed inset-0 z-40 bg-white overflow-y-auto" style={{ top: '64px', left: '256px' }} data-testid="assessment-overlay">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-slate-200 px-6 py-3 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Learning Maturity Assessment{selectedOrgName ? ` for ${selectedOrgName}` : ''}
          </h2>
          <p className="text-xs text-slate-500">
            {step === 'org-select' ? 'Select the organization you wish to assess' :
              result ? 'Assessment complete' :
                `Pillar ${currentPillar + 1} of ${pillars.length} — ${answeredCount}/${totalDimensions} answered`}
          </p>
          {step === 'questions' && !result && (
            <p className="text-xs text-slate-400 mt-0.5">For each question, select the statement that best describes your organization's current state — not where you aspire to be.</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          {step === 'questions' && !result && (
            <Progress value={progress} className="w-40 h-2" />
          )}
          <Button variant="ghost" size="sm" onClick={result ? handleDone : onClose} data-testid="close-overlay-btn">
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Org selection step */}
        {step === 'org-select' && isSuperAdmin && (
          <div className="max-w-md mx-auto text-center py-12" data-testid="org-select-step">
            <Building2 className="h-10 w-10 text-teal-500 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Select Organization</h3>
            <p className="text-sm text-slate-500 mb-6">Choose which organization to assess. The assessment will be tied to this organization for audit purposes.</p>
            <Select value={selectedOrg} onValueChange={setSelectedOrg}>
              <SelectTrigger className="w-full mb-4" data-testid="overlay-org-selector">
                <SelectValue placeholder="Select an organization" />
              </SelectTrigger>
              <SelectContent>
                {organizations.map(org => (
                  <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setStep('questions')} disabled={!selectedOrg} className="bg-teal-600 hover:bg-teal-700" data-testid="start-assessment-btn">
              Start Assessment <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        )}

        {/* Results step */}
        {step === 'questions' && result && (
          <div className="max-w-lg mx-auto py-8" data-testid="assessment-result-step">
            <div className="text-center mb-6">
              <div className="relative w-36 h-36 mx-auto mb-4">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="#e2e8f0" strokeWidth="5" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke={getBarColor(result.overall_score)} strokeWidth="5"
                    strokeDasharray={`${result.overall_score * 2.64} 264`} strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-4xl font-extrabold text-slate-900">{Math.round(result.overall_score)}</span>
                  <span className="text-xs text-slate-500 uppercase tracking-wider mt-1">LEI Score</span>
                </div>
              </div>
              <Badge className={cn("text-sm px-3 py-1", getBandColor(result.maturity_band))}>{result.maturity_band} Maturity</Badge>
              {result.assessor_name && (
                <p className="text-xs text-slate-400 mt-2">Assessed by {result.assessor_name} on {new Date(result.created_at).toLocaleString()}</p>
              )}
            </div>

            <div className="space-y-2 mb-6">
              {result.pillar_scores.map(ps => (
                <div key={ps.pillar_id} className="flex items-center gap-3 p-3 rounded-lg border border-slate-100">
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-slate-700">{ps.name}</span>
                      <span className="text-sm font-bold" style={{ color: getBarColor(ps.score) }}>{Math.round(ps.score)}</span>
                    </div>
                    <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full rounded-full" style={{ width: `${ps.score}%`, backgroundColor: getBarColor(ps.score) }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              <Button onClick={handleDone} className="flex-1 bg-teal-600 hover:bg-teal-700" data-testid="done-btn">
                View Full Analysis
              </Button>
            </div>
          </div>
        )}

        {/* Questions step */}
        {step === 'questions' && !result && (
          <>
            {/* Pillar tabs */}
            <div className="flex gap-1 overflow-x-auto pb-2 mb-4">
              {pillars.map((p, i) => {
                const Icon = PILLAR_ICONS[p.id] || BarChart3;
                const pAnswered = p.dimensions.filter(d => answers[d.id] !== undefined).length;
                const isComplete = pAnswered === p.dimensions.length;
                return (
                  <button
                    key={p.id}
                    onClick={() => setCurrentPillar(i)}
                    className={cn(
                      "flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-all border",
                      currentPillar === i
                        ? 'bg-teal-50 border-teal-300 text-teal-800'
                        : isComplete
                          ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                          : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                    )}
                    data-testid={`overlay-pillar-tab-${p.id}`}
                  >
                    {isComplete ? <Check className="h-3 w-3" /> : <Icon className="h-3 w-3" />}
                    {p.name.split('&')[0].trim()}
                    <span className="text-xs opacity-60">{pAnswered}/{p.dimensions.length}</span>
                  </button>
                );
              })}
            </div>

            {/* Questions */}
            {pillar && (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Pillar {currentPillar + 1}</span>
                  <span className="text-xs text-slate-400">|</span>
                  <span className="text-sm font-semibold text-slate-800">{pillar.name}</span>
                  <Badge variant="outline" className="text-xs ml-auto">{pillarAnswered}/{pillar.dimensions.length}</Badge>
                </div>

                <div className="space-y-5">
                  {pillar.dimensions.map((dim, di) => {
                    const selected = answers[dim.id];
                    return (
                      <div
                        key={dim.id}
                        className={cn("p-4 rounded-lg border transition-all", selected !== undefined ? 'border-teal-200 bg-teal-50/30' : 'border-slate-200')}
                        data-testid={`overlay-question-${dim.id}`}
                      >
                        <p className="text-sm font-medium text-slate-800 mb-3">
                          <span className="text-xs text-slate-400 mr-2">{di + 1}.</span>
                          {dim.question}
                        </p>
                        <div className="space-y-1.5">
                          {dim.options.map(opt => (
                            <button
                              key={opt.level}
                              onClick={() => handleSelect(dim.id, opt.level)}
                              className={cn(
                                "w-full text-left p-3 rounded-md border text-sm transition-all",
                                selected === opt.level
                                  ? 'border-teal-400 bg-teal-100/60 text-teal-900 font-medium'
                                  : 'border-slate-100 hover:border-slate-300 hover:bg-slate-50 text-slate-600'
                              )}
                              data-testid={`overlay-option-${dim.id}-${opt.level}`}
                            >
                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border text-xs mr-2 flex-shrink-0"
                                style={{
                                  borderColor: selected === opt.level ? '#0d9488' : '#cbd5e1',
                                  backgroundColor: selected === opt.level ? '#0d9488' : 'transparent',
                                  color: selected === opt.level ? 'white' : '#64748b',
                                }}
                              >
                                {selected === opt.level ? <Check className="h-3 w-3" /> : opt.level}
                              </span>
                              {opt.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Navigation */}
                <div className="flex items-center justify-between mt-6 pt-4 border-t">
                  <Button variant="outline" size="sm" disabled={currentPillar === 0} onClick={() => setCurrentPillar(prev => prev - 1)} data-testid="overlay-prev-btn">
                    <ChevronLeft className="h-4 w-4 mr-1" /> Previous
                  </Button>
                  {currentPillar < pillars.length - 1 ? (
                    <Button size="sm" onClick={() => setCurrentPillar(prev => prev + 1)} data-testid="overlay-next-btn">
                      Next Pillar <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      disabled={!allAnswered || submitting}
                      onClick={handleSubmit}
                      className="bg-teal-600 hover:bg-teal-700"
                      data-testid="overlay-submit-btn"
                    >
                      {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ClipboardCheck className="h-4 w-4 mr-1" />}
                      Calculate & Save
                    </Button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
